import { defineStore } from 'pinia'
import _ from 'lodash'

import { useUnitStore } from '@/store/unit'
import type Field from '@/types/field'
import { urlHashHandler } from '@/utils/urlHashHandler'

import { useProjectStore } from '@/store/project'
import type Unit from '@/types/unit'

import { filterFunction } from '@/utils/filterTypes/FilterHandlers'

export default interface Filter {
    field: Field
    allValues: any[]
    decisionMakingValues: any[]
    conditionsInUrl: any
    hide: boolean
}

export const useFilterStore = defineStore('filterStore', () => {
    const MAX_NUMBER_OF_ITEMS_FOR_TOGGLE_BUTTONS = 7
    const FIELDS_THAT_CAN_USE_TOGGLE_BUTTONS = ['rooms', 'floor']

    const filters = ref({})

    const allFilterFields = ref<Field[]>([])
    const listViewFilterFields = ref<Field[]>([])

    const allFilters = ref<Filter[]>([])
    const filtersInUrl = ref<any[]>([])

    const unitStatusIsFilteredByDefault = (unitStatus): Boolean => {
        const { projectStatuses: statues } = useProjectStore()

        const status = statues.find((status) => status.id === unitStatus)
        if (!status) return false

        return status.isFilteredByDefault
    }

    const filteredUnits = computed(() => {
        const units = useUnitStore().units

        const filteredUnits = units.filter((singleUnit: Unit) => {
            let check = true

            if (unitStatusIsFilteredByDefault(singleUnit.status)) {
                return false
            }

            if (filtersInUrl.value.length > 0) {
                for (let index = 0; index < filtersInUrl.value.length; index++) {
                    const filter = allFilters.value.find(
                        (f) => f.field.key === filtersInUrl.value[index].key
                    )
                    if (!filter) continue

                    if (!filter.conditionsInUrl) continue

                    check = filterFunction(singleUnit, filter)
                    if (!check) break
                }
            }
            return check
        })

        return filteredUnits
    })

    const count = computed(() => {
        return {
            total: useUnitStore().units.length,
            filtered: filteredUnits.value.length
        }
    })

    const getCurrentFilterStatus = (filterableFields: Field[]) => {
        const filtersInUrl: any[] = []

        for (let index = 0; index < filterableFields.length; index++) {
            const field = filterableFields[index]

            let check = urlHashHandler.hasKey(`f:${field.key}`)
                ? urlHashHandler.getKey(`f:${field.key}`)
                : undefined

            if (check) {
                filtersInUrl.push({ key: field.key, conditions: check })
            }
        }

        return filtersInUrl
    }

    const getFilterValuesForField = (filterField: Field) => {
        // all Units
        const allUnits = useUnitStore().units

        let filterValues = []

        for (let index = 0; index < allUnits.length; index++) {
            const unit = allUnits[index]

            const flattenedUnit = { ...unit, ...unit.rowData, ...unit.customColumnValues }

            if (flattenedUnit[filterField.key]) {
                filterValues.push(flattenedUnit[filterField.key])
                continue
            }
        }

        if (filterValues && filterField.type !== 'string') {
            return _.uniq(filterValues).sort((a, b) => a - b)
        }

        return _.uniq(filterValues)
    }

    const countAppliedFilters = computed(() => {
        let num = 0
        if (filtersInUrl.value) {
            num = filtersInUrl.value.length
        }

        return num
    })

    const initializeFilter = (setDefaultFilters: boolean) => {
        const { filterableFields } = useProjectStore()
        allFilterFields.value = filterableFields
        listViewFilterFields.value = filterableFields
        allFilters.value = filterableFields.map((field) => {
            const allValues = getFilterValuesForField(field)
            return {
                field: field,
                allValues: allValues,
                decisionMakingValues: null,
                conditionsInUrl: null,
                hide: false
            }
        })
        const currentFilterStatus = getCurrentFilterStatus(filterableFields)
        if (currentFilterStatus) {
            filtersInUrl.value = currentFilterStatus
            filtersInUrlToFilter()
        } else {
            filtersInUrl.value = []
            setAllFiltersToUrl()
        }
    }

    const filtersInUrlToFilter = () => {
        for (let index = 0; index < filtersInUrl.value.length; index++) {
            const f = filtersInUrl.value[index]
            const filterIndex = allFilters.value.findIndex((filter) => {
                if (filter.field.key === f.key) {
                    return true
                }
                return false
            })

            if (filterIndex !== -1) {
                allFilters.value[filterIndex].conditionsInUrl = f.conditions
            }
        }
    }

    const updateFilter = (field, newDecisionMakingValues) => {
        const filterIndex = allFilters.value.findIndex((f) => f.field.key === field.key)
        if (filterIndex !== -1) {
            allFilters.value[filterIndex].conditionsInUrl = newDecisionMakingValues
            allFilters.value[filterIndex].decisionMakingValues = newDecisionMakingValues
        }
        setFilterParametersToUrl(field, newDecisionMakingValues)
        const { filterableFields } = useProjectStore()
        const currentFilterStatus = getCurrentFilterStatus(filterableFields)
        if (currentFilterStatus) {
            filtersInUrl.value = currentFilterStatus
            filtersInUrlToFilter()
        }
    }

    const resetFilters = () => {
        filtersInUrl.value = []
        for (let index = 0; index < allFilters.value.length; index++) {
            const f = allFilters.value[index]
            f.decisionMakingValues = null
            f.conditionsInUrl = null
            allFilters.value[index] = f
        }

        setAllFiltersToUrl()
    }

    const setAllFiltersToUrl = () => {
        for (let index = 0; index < allFilters.value.length; index++) {
            const f = allFilters.value[index]
            setFilterParametersToUrl(f.field, f.decisionMakingValues)
        }
    }

    const setFilterParametersToUrl = (filterField: Field, decisionMakingValues: any[]) => {
        if (!filterField) return
        if (
            decisionMakingValues === null ||
            decisionMakingValues.length === 0 ||
            decisionMakingValues === undefined
        ) {
            urlHashHandler.unsetKey(`f:${filterField.key}`)
            return
        } else {
            urlHashHandler.setKey(`f:${filterField.key}`, decisionMakingValues)
            return
        }
    }

    const displayToggleButtons = (filter: Filter) => {
        if (
            filter.field.isSystem === true &&
            FIELDS_THAT_CAN_USE_TOGGLE_BUTTONS.includes(filter.field.key) &&
            filter.allValues.length <= MAX_NUMBER_OF_ITEMS_FOR_TOGGLE_BUTTONS
        )
            return true
    }

    return {
        filters,
        allFilterFields,
        allFilters,
        filtersInUrl,
        listViewFilterFields,
        initializeFilter,
        filteredUnits,
        getFilterValuesForField,
        updateFilter,
        countAppliedFilters,
        count,
        resetFilters,
        displayToggleButtons
    }
})
